import { canv } from "@/canv/global.js";
import "@/widgetMenu/wm_media";
import "@/widgetMenu/wm_arrowEnd";
import "@/widgetMenu/wm_stickerColor/wm_stickerColor.ts";
import "@/widgetMenu/wm_align/wm_align.js";
import "@/widgetMenu/wm_textBold/wm_textBold.js";
import "@/widgetMenu/wm_border/WmBorder";
import "@/widgetMenu/wm_textSize/WmTextSize";
import "@/widgetMenu/wm_shape/WmShape";
import "@/widgetMenu/wm_textBulleted/WmTextBulleted";
import "@/widgetMenu/wm_group/wm_group.js";
import "@/widgetMenu/wm_width/wm_width";
import "@/widgetMenu/wm_textStrike";
import "@/widgetMenu/wm_textAlign";
import "@/widgetMenu/widgetMenu";
import "@/widgetMenu/wm_color";
import "@/widgetMenu/wm_lineType";
import "@/widgetMenu/wm_textBackground/wm_textBackground.js";
import "@/widgetMenu/wm_addText/Wm_addText.ts";
import "@/widgetMenu/wm_clip";
import "@/widgetMenu/wm_alignChilds";
import "@/widgetMenu/wm_filter/wm_filter.js";

import { isSeveralEntityTypesInSelected, reverseTransformPoint } from "@/canv/utils.js";
import { getFirstSelected } from "@/canvasController/helpers";
import { AI_ASSISTANT_SUPPORTED_WIDGETS } from "@/constants";
import type { ViewMode } from "@/elements/documentViewer/DocumentViewer";
import { EmbedWidget } from "@/elements/embed";
import type { tableWidgetCell } from "@/elements/table/tableCell";
import type { Group } from "@/group";
import { WmAddText } from "@/widgetMenu/wm_addText";
import { WmAlign } from "@/widgetMenu/wm_align";
import { WmAlignChilds } from "@/widgetMenu/wm_alignChilds";
import { WmArrowEnd } from "@/widgetMenu/wm_arrowEnd";
import { wmBorder } from "@/widgetMenu/wm_border";
import type { BorderOptions } from "@/widgetMenu/wm_border/WmBorder";
import { wmCheck } from "@/widgetMenu/wm_check/WmCheck";
import { WmClip } from "@/widgetMenu/wm_clip";
import { WmColor } from "@/widgetMenu/wm_color";
import { WmContextMenu } from "@/widgetMenu/wm_contextMenu";
import { WmDelete } from "@/widgetMenu/wm_delete";
import { WmDocumentDownload } from "@/widgetMenu/wm_documentDownload";
import { WmDocumentMode } from "@/widgetMenu/wm_documentMode";
import { WmDocumentPreviewPage } from "@/widgetMenu/wm_documentPreviewPage";
import { WmDocumentReName } from "@/widgetMenu/wm_documentReName";
import { WmDocumentTurnPage } from "@/widgetMenu/wm_documentTurnPage";
import { WmFilter } from "@/widgetMenu/wm_filter";
import { WmGroup } from "@/widgetMenu/wm_group";
import { WmHide } from "@/widgetMenu/wm_hide";
import { WmLineType } from "@/widgetMenu/wm_lineType";
import { wmLink } from "@/widgetMenu/wm_link";
import { WmLock } from "@/widgetMenu/wm_lock";
import { WmMedia } from "@/widgetMenu/wm_media";
import { WmMindMapDirection } from "@/widgetMenu/wm_mindMapDirection";
import { WmShape } from "@/widgetMenu/wm_shape";
import { WmStickerColor } from "@/widgetMenu/wm_stickerColor";
import { WmTextAlign } from "@/widgetMenu/wm_textAlign";
import { WmTextBackground } from "@/widgetMenu/wm_textBackground";
import { WmTextBold } from "@/widgetMenu/wm_textBold";
import { wmTextBulleted } from "@/widgetMenu/wm_textBulleted";
import { wmTextSize } from "@/widgetMenu/wm_textSize/WmTextSize";
import { WmTextStrike } from "@/widgetMenu/wm_textStrike";
import { wmWidth } from "@/widgetMenu/wm_width/wm_width";
import { wmAiAssistant } from "./wm_aiAssistant";
import { wmEdit } from "./wm_edit/wm_edit";
import { WmMerge } from "./wm_merge";
import { WmUnmerge } from "./wm_unmerge";

function isWidgetsSupportsAi(groups: Group[]) {
    for (const group of groups) {
        if (group.isItemWrapper()) {
            const child = group.onlyChild();

            if (child && AI_ASSISTANT_SUPPORTED_WIDGETS.includes(child.constructorName)) {
                return true;
            }

            continue;
        }

        for (const innerGroup of group.children) {
            if ("onlyChild" in innerGroup) {
                const child = innerGroup.onlyChild();

                if (child && AI_ASSISTANT_SUPPORTED_WIDGETS.includes(child.constructorName)) {
                    return true;
                }
            }
        }
    }

    return false;
}

type Processor =
    | WmShape
    | WmColor
    | WmTextBold
    | WmTextStrike
    | wmTextBulleted
    | WmTextAlign
    | wmTextSize
    | WmGroup
    | wmBorder
    | wmWidth
    | WmArrowEnd
    | WmStickerColor
    | WmMedia
    | WmAlign
    | WmLineType
    | WmTextBackground
    | WmAddText
    | WmClip
    | WmAlignChilds
    | WmHide
    | WmLock
    | wmCheck
    | WmMindMapDirection
    | wmLink
    | WmFilter
    | WmContextMenu
    | WmDelete
    | wmEdit
    | WmMerge
    | WmUnmerge
    | wmAiAssistant
    | WmDocumentMode
    | WmDocumentPreviewPage
    | WmDocumentTurnPage
    | WmDocumentDownload
    | WmDocumentReName;

//todo все типы и интерфейсы переедут в отдельный файл когда будет заканчиваться перенос на TS
export type LineType = "straight" | "quadratic" | "curved";
export type DashType = "solid" | "dashed" | "dotted";

export interface LineOption {
    lineType: LineType;
    lineWidth: number;
    dashType: DashType;
}

export interface DocumentPreviewPageOption {
    previewPageNumber: number;
    currentPageNumber: number;
}

export interface DocumentTurnPageOption {
    currentPageNumber: number;
    totalPages: number;
}

export class widgetOption<
    T =
        | string
        | number
        | null
        | object
        | boolean
        | LineOption
        | BorderOptions
        | DocumentPreviewPageOption
        | DocumentTurnPageOption,
> {
    constructor(name: string, value: T, UID: string) {
        this.name = name;
        this.value = value;
        this.UID = UID;
    }

    getState() {
        return {
            value: this.value,
            name: this.name,
            UID: this.UID,
        };
    }

    name: string;
    value: T;
    UID: string;
}

export class widgetMenu {
    // external API

    // тяжелая функция из-за работы с DOM
    reopen() {
        if (canv.isBoardModeViewCopy()) {
            return;
        }

        if (canv.canvasController.selected.length === 0) {
            this.hide(); // если нету выбранных entity убеждаемся, что и меню нету. Нужно, так как после deleteProcessor вызывается reopen, а не hide
            return;
        }
        this.hideSubMenu(); // subMenu нужно прятать всегда, так как может поменяться список menuItems и при TryLocateMenu не найтись subMenuBoss

        this.allOptions = [];
        this.proccessors = [];

        const options: Array<widgetOption[]> = [];
        for (let p = 0; p < canv.canvasController.selected.length; p++) {
            canv.canvasController.selected[p].collectWidgetOptions(options);
        }

        const optionsUniqs: Array<string[]> = [];
        const plainOptions: widgetOption[] = [];
        for (let i = 0; i < options.length; i++) {
            const uniqs: string[] = [];
            for (let k = 0; k < options[i].length; k++) {
                plainOptions.push(options[i][k]);
                const index = uniqs.indexOf(options[i][k].name);
                if (index === -1) {
                    uniqs.push(options[i][k].name);
                }
            }
            optionsUniqs.push(uniqs);
        }

        for (let i = 0; i < plainOptions.length; i++) {
            const name = plainOptions[i].name;
            let foundInEvery = true;
            for (let k = 0; k < optionsUniqs.length; k++) {
                const index = optionsUniqs[k].indexOf(name);
                if (index === -1) {
                    foundInEvery = false;
                }
            }

            if (foundInEvery) {
                this.allOptions.push(plainOptions[i]);
            }
        }

        const optionsNames: string[] = [];
        const optionGroups: Array<widgetOption[]> = [];

        // group by proccessorType
        for (let i = 0; i < this.allOptions.length; i++) {
            let groupNumber = -1;
            for (let k = 0; k < optionsNames.length; k++) {
                if (optionsNames[k] === this.allOptions[i].name) {
                    groupNumber = k;
                    break;
                }
            }

            if (groupNumber !== -1) {
                optionGroups[groupNumber].push(this.allOptions[i]);
            } else {
                optionsNames.push(this.allOptions[i].name);
                optionGroups.push([this.allOptions[i]]);
            }
        }

        let menuDiv = document.querySelector<HTMLElement>(".widgetMenu");

        if (!menuDiv) {
            menuDiv = document.createElement("div");
            menuDiv.classList.add("widgetMenu");
            menuDiv.style.opacity = "0";
            menuDiv.style.marginTop = "10px";
            document.body.prepend(menuDiv);
        } else {
            menuDiv.innerHTML = "";
        }

        for (let i = 0; i < optionsNames.length; i++) {
            const newProcessor = this.getProccessorByName(optionsNames[i], optionGroups[i]);
            if (newProcessor) {
                menuDiv.innerHTML += newProcessor.build();
                menuDiv.innerHTML += `<div class="wm_spacer"></div>`;
                this.proccessors.push(newProcessor);
            }
        }

        let useGroupOptions = true;
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        if (cursorTool) {
            const isOnlyEmbedWidget =
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0].onlyChild() instanceof EmbedWidget;

            if (cursorTool.isMode("interaction") && !isOnlyEmbedWidget) {
                useGroupOptions = false;
            } else if (
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0]?.onlyChild()?.constructorName === "comment"
            ) {
                useGroupOptions = false;
            }
        }

        if (useGroupOptions) {
            // align
            if (canv.canvasController.selected.length !== 1) {
                const groupProcessor = this.getProccessorByName("align", null);
                if (this.proccessors.length !== 0) {
                    menuDiv.innerHTML += `<div class="wm_dividor"></div>`;
                }
                if (groupProcessor) {
                    menuDiv.innerHTML += groupProcessor.build();
                    this.proccessors.push(groupProcessor);
                }
            }

            // group
            if (
                canv.canvasController.selected.length !== 1 ||
                (!canv.canvasController.selected[0].isItemWrapper() &&
                    !canv.canvasController.selected[0].isLocked)
            ) {
                const groupProcessor = this.getProccessorByName("group", null);
                if (groupProcessor) {
                    menuDiv.innerHTML += groupProcessor.build();
                    this.proccessors.push(groupProcessor);
                }
            }

            // lock ----
            const lockProcessor = this.getProccessorByName("lock", null);
            if (lockProcessor) {
                menuDiv.innerHTML += lockProcessor.build();
                this.proccessors.push(lockProcessor);
            }

            // filter ----
            if (isSeveralEntityTypesInSelected(canv.canvasController.selected)) {
                const filterProcessor = this.getProccessorByName("filter", null);
                if (filterProcessor) {
                    menuDiv.innerHTML += filterProcessor.build();
                    this.proccessors.push(filterProcessor);
                }
            }
            // ----------

            // delete ----
            const deleteProcessor = this.getProccessorByName("delete", null);
            if (deleteProcessor) {
                menuDiv.innerHTML += deleteProcessor.build();
                this.proccessors.push(deleteProcessor);
            }

            // aiAssistant ----
            if (
                !canv.getInitDataProp("isOnPrem") &&
                isWidgetsSupportsAi(canv.canvasController.selected)
            ) {
                const aiAssistantProcessor = this.getProccessorByName("aiAssistant", null);
                if (aiAssistantProcessor) {
                    menuDiv.innerHTML += aiAssistantProcessor.build();
                    this.proccessors.push(aiAssistantProcessor);
                }
            }

            // context menu ----
            const contextMenuProcessor = this.getProccessorByName("contextMenu", null);
            if (contextMenuProcessor) {
                menuDiv.innerHTML += contextMenuProcessor.build();
                this.proccessors.push(contextMenuProcessor);
            }
            // ----------
        }

        for (let i = 0; i < this.proccessors.length; i++) {
            this.proccessors[i].init();
        }

        setTimeout(this.TryLocateMenu.bind(this), 50);
    }

    hide() {
        const menu = document.querySelector(".widgetMenu");
        if (menu) {
            menu.outerHTML = "";
        }
        this.hideSubMenu();
        this.proccessors = [];
    }

    hideSubMenu() {
        const subMenu = document.querySelector(".widgetSubMenu");
        if (subMenu) {
            subMenu.outerHTML = "";
        }
    }

    TryLocateMenu() {
        const selectedProps = canv.canvasController.getSelectedProps();

        const startX = selectedProps.startX;
        const startY = selectedProps.startY;
        const width = selectedProps.width;

        const menu = document.querySelector<HTMLElement>(".widgetMenu");
        if (!menu || menu?.innerHTML === "") {
            return;
        }

        if (menu.style.opacity === "0") {
            menu.style.opacity = "1";
            menu.style.marginTop = "0px";
        }

        const menuSize = menu.getBoundingClientRect();

        let xTarget = (startX + width / 2) * canv.m[0];
        let yTarget = startY * canv.m[3];

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        if (cursorTool.isMode("interaction")) {
            const interactionLocale = getFirstSelected().locateWidgetMenu();
            if (interactionLocale?.status) {
                xTarget = interactionLocale.x * canv.m[3];
                yTarget = interactionLocale.y * canv.m[3];
            }
        }

        const pt = reverseTransformPoint(xTarget, yTarget);

        let wantedX = pt.x - menuSize.width / 2;
        if (wantedX < 10) wantedX = 10;
        if (wantedX + menuSize.width + 10 > canv.canvas.getBoundingClientRect().width)
            wantedX = canv.canvas.getBoundingClientRect().width - menuSize.width - 10;
        menu.style.left = `${(wantedX).toString()}px`;

        const gap = 50; //  было 25
        let wantedY = pt.y - menuSize.height - gap;
        if (wantedY < 60) wantedY = 60;
        if (wantedY + menuSize.height + 10 > canv.canvas.getBoundingClientRect().height)
            wantedY = canv.canvas.getBoundingClientRect().height - menuSize.height - 10;
        menu.style.top = `${(wantedY).toString()}px`;

        const subMenu = document.querySelector<HTMLElement>(".widgetSubMenu");
        if (!subMenu) {
            return;
        }

        if (subMenu.style.opacity === "0") {
            subMenu.style.opacity = "1";
        }

        const menuItem = document.getElementsByClassName(this.subMenuBoss)[0];

        subMenu.style.left = `${(
            menuItem.getBoundingClientRect().left +
                menuItem.getBoundingClientRect().width / 2 -
                subMenu.getBoundingClientRect().width / 2
        ).toString()}px`;
        subMenu.style.top = `${(
            menuItem.getBoundingClientRect().top + menuItem.getBoundingClientRect().height + 10
        ).toString()}px`;

        if (this.colorPickOpen) {
            const pallet = document.getElementById("clr-picker");
            if (pallet) {
                const palletItem = document.querySelectorAll(".wm_color_submenu_item")[17];
                pallet.style.left = `${palletItem.getBoundingClientRect().left}px`;
                pallet.style.top = `${
                    palletItem.getBoundingClientRect().top +
                    palletItem.getBoundingClientRect().height +
                    4
                }px`;
            }
        }
    }

    // helpers

    getProccessorByName(name: string, options: widgetOption[] | null) {
        if (name === "shape") return new WmShape(options as widgetOption<string>[]);
        if (name === "color") return new WmColor(options as widgetOption<string>[]);
        if (name === "textColor")
            return new WmColor(options as widgetOption<string>[], { isText: true });
        if (name === "textBold") return new WmTextBold(options as widgetOption<string>[]);
        if (name === "textStrike") return new WmTextStrike(options as widgetOption<boolean>[]);
        if (name === "textBulleted") return new wmTextBulleted(options as widgetOption<boolean>[]);
        if (name === "textAlign") return new WmTextAlign(options as widgetOption<string>[]);
        if (name === "textSize") return new wmTextSize(options as widgetOption<number>[]);
        if (name === "group") return new WmGroup();
        if (name === "border") return new wmBorder(options as widgetOption<BorderOptions>[]);
        if (name === "width") return new wmWidth(options as widgetOption<number>[]);
        if (name === "arrowEnd") return new WmArrowEnd(options as widgetOption<string>[]);
        if (name === "arrowEndFirst")
            return new WmArrowEnd(options as widgetOption<string>[], "first");
        if (name === "stickerColor") return new WmStickerColor(options as widgetOption<string>[]);
        if (name === "media") return new WmMedia(options as widgetOption<string>[]);
        if (name === "align") return new WmAlign(options as widgetOption<string>[]);
        if (name === "lineType") return new WmLineType(options as widgetOption<LineOption>[]);
        if (name === "textBackground") return new WmTextBackground(options as widgetOption[]);
        if (name === "addText") return new WmAddText(options as widgetOption<object>[]);
        if (name === "clip") return new WmClip(options as widgetOption<string>[]);
        if (name === "alignChilds") return new WmAlignChilds(options as widgetOption<string>[]);
        if (name === "hide") return new WmHide(options as widgetOption<boolean>[]);
        if (name === "lock") return new WmLock(options as widgetOption[]);
        if (name === "check") return new wmCheck(options as widgetOption<boolean>[]);
        if (name === "mindMapDirection")
            return new WmMindMapDirection(options as widgetOption<string>[]);
        if (name === "link") return new wmLink(options as widgetOption<string>[], false);
        if (name === "linkTo") return new wmLink(options as widgetOption<string>[], true);
        if (name === "filter") return new WmFilter();
        if (name === "delete") return new WmDelete();
        if (name === "contextMenu") return new WmContextMenu();
        if (name === "mobileEdit") return new wmEdit(options);
        if (name === "merge") return new WmMerge(options as widgetOption<tableWidgetCell>[]);
        if (name === "unmerge") return new WmUnmerge(options as widgetOption<tableWidgetCell>[]);
        if (name === "aiAssistant") return new wmAiAssistant(options as widgetOption<string>[]);
        if (name === "documentMode") return new WmDocumentMode(options as widgetOption<ViewMode>[]);
        if (name === "documentPreviewPage")
            return new WmDocumentPreviewPage(options as widgetOption<DocumentPreviewPageOption>[]);
        if (name === "documentTurnPage")
            return new WmDocumentTurnPage(options as widgetOption<DocumentTurnPageOption>[]);
        if (name === "documentDownload") return new WmDocumentDownload();
        if (name === "documentName") return new WmDocumentReName(options as widgetOption<string>[]);

        return null;
    }

    allOptions: widgetOption[] = [];
    proccessors: Processor[] = [];
    subMenuBoss = "";
    colorPickOpen = false;
}
